import type { Query, QueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import {
  getPositionListContainer as getListContainer,
  rebuildPositionCaches,
} from "@/features/positions/utils/position-list-container";
import {
  completeGlobalFlattenAttempt,
  hasLiveGlobalFlattenAttempt,
  markGlobalFlattenUnknown,
} from "@/features/positions/utils/global-flatten-safe-state";
import { captureAlertToast, shouldToastOnce } from "./toast-once";
import { transferClosedPositionRealizedPnl } from "./account-realized-pnl-cache";
import { isInstantClose } from "@/features/positions/utils/instant-close";
import {
  isKnownDirection,
  readDirection,
} from "@/features/positions/utils/direction-display";
import { isSignCompletePosition } from "@/features/positions/utils/sign-complete";

/**
 * WS-B payload-apply (real-time base, 2026-07-14).
 *
 * The unified socket's position events carry the data the dashboard needs
 * (position_id, action, quantity, prices, PnL) — but the positions arm used to
 * discard the payload and invalidate react-query, forcing a REST refetch on
 * every event (poll cadence + a full network round-trip of added latency).
 *
 * This module applies position deltas STRAIGHT into the react-query cache —
 * the same direct `setQueriesData` mutation the market-data PnL path already
 * uses (`handle-market-updates.ts`) — so a fill/close renders in the next
 * frame. Reconciliation still happens via a TRAILING debounced invalidation
 * (bursts of 14 follower fills → one refetch, not fourteen).
 *
 * Event contract (backend `event_publisher.publish_position_update`):
 *   type: "position.opened" | "position.updated" | "position.closed"
 *   payload: { position_id, symbol, action, quantity, entry_price?,
 *              current_price?, unrealized_pnl?, realized_pnl?, side?, account_id? }
 */

type AnyRecord = Record<string, any>;

// Profile positions use an array and camelCase; normalize at this adapter only.
const getPositionListContainer = (data: AnyRecord | undefined): { positions: AnyRecord[]; rebuild: (positions: AnyRecord[]) => AnyRecord } | null => {
  if (!Array.isArray(data)) return getListContainer(data);
  const rows = data.map((row: AnyRecord) => ({
    ...row,
    broker_position_id: row.broker_position_id ?? row.brokerPositionId,
    account_id: row.account_id ?? row.accountId,
    direction: row.direction ?? row.side,
    open_price: row.open_price ?? row.entryPrice,
    current_price: hasOwn(row, "current_price") ? row.current_price : row.currentPrice,
    stop_loss: hasOwn(row, "stop_loss") ? row.stop_loss : row.stopLoss,
    take_profit: hasOwn(row, "take_profit") ? row.take_profit : row.takeProfit,
    volume: row.volume ?? String(row.quantity),
    status: row.status ?? "active",
    pnl: hasOwn(row, "pnl") ? row.pnl : row.unrealizedPnl == null ? null : String(row.unrealizedPnl),
    unrealized_pnl: hasOwn(row, "unrealized_pnl") ? row.unrealized_pnl : row.unrealizedPnl,
  }));
  return {
    positions: rows,
    rebuild: (positions: AnyRecord[]): AnyRecord => positions.map((row) => ({
      ...row,
      unrealizedPnl: row.unrealized_pnl,
      currentPrice: finiteNumber(row.current_price),
      stopLoss: finiteNumber(row.stop_loss),
      takeProfit: finiteNumber(row.take_profit),
      side: row.direction,
      entryPrice: finiteNumber(row.open_price),
    })),
  };
};

const hasOwn = (value: AnyRecord, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const finiteNumber = (value: unknown): number | null => {
  if ((typeof value !== "number" && typeof value !== "string") || String(value).trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const asObservedSeq = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

const asBrokerMarkSource = (value: unknown): string | null =>
  value === "broker_anchor" || value === "broker_quote" || value === "broker_last_good"
    ? value
    : null;

const compareDecimalComponent = (left: string, right: string): number => {
  const normalizedLeft = left.replace(/^0+/, "") || "0";
  const normalizedRight = right.replace(/^0+/, "") || "0";
  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length > normalizedRight.length ? 1 : -1;
  }
  return normalizedLeft === normalizedRight ? 0 : normalizedLeft > normalizedRight ? 1 : -1;
};

const observedSeqParts = (value: string): string[] | null => {
  if (/^\d+$/.test(value)) return [value];
  const projectX = /^(\d+)-(\d+)$/.exec(value);
  return projectX ? [projectX[1], projectX[2]] : null;
};

const compareObservedSeq = (left: string, right: string): number | null => {
  const leftParts = observedSeqParts(left);
  const rightParts = observedSeqParts(right);
  if (!leftParts || !rightParts || leftParts.length !== rightParts.length) {
    return null;
  }
  for (let index = 0; index < leftParts.length; index += 1) {
    const ordering = compareDecimalComponent(leftParts[index], rightParts[index]);
    if (ordering !== 0) return ordering;
  }
  return 0;
};

const parseMeasuredAt = (value: unknown): number | null => {
  if (typeof value !== "string" || value.trim() === "") return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const rejectsLifecycleDelta = (row: AnyRecord, payload: AnyRecord): boolean => {
  const currentLifecycle = asObservedSeq(row.lifecycle_key);
  const incomingLifecycle = asObservedSeq(payload.lifecycle_key);
  if (currentLifecycle && incomingLifecycle && currentLifecycle !== incomingLifecycle) {
    return String(row.status ?? "").toLowerCase() !== "closed";
  }
  const currentSeq = asObservedSeq(row.observed_seq);
  const incomingSeq = asObservedSeq(payload.observed_seq);
  const sameSequenceContext =
    currentLifecycle !== null &&
    incomingLifecycle !== null &&
    currentLifecycle === incomingLifecycle;
  if (sameSequenceContext && currentSeq && incomingSeq) {
    const ordering = compareObservedSeq(incomingSeq, currentSeq);
    if (ordering !== null) return ordering <= 0;
  }
  const currentMeasuredAt = parseMeasuredAt(row.mark_measured_at);
  const incomingMeasuredAt = parseMeasuredAt(payload.mark_measured_at);
  if (currentSeq && payload.mark_state != null && !incomingSeq && incomingMeasuredAt === null) return true;
  return (
    currentMeasuredAt !== null &&
    incomingMeasuredAt !== null &&
    incomingMeasuredAt <= currentMeasuredAt
  );
};

const isNewerOpenedLifecycle = (row: AnyRecord, payload: AnyRecord): boolean => {
  const currentSeq = asObservedSeq(row.observed_seq);
  const incomingSeq = asObservedSeq(payload.observed_seq);
  if (currentSeq && incomingSeq) {
    const ordering = compareObservedSeq(incomingSeq, currentSeq);
    if (ordering !== null) return ordering > 0;
  }
  const currentOpenedAt = parseMeasuredAt(
    row.opened_at ?? row.open_time ?? row.timestamp,
  );
  const incomingOpenedAt = parseMeasuredAt(
    payload.opened_at ?? payload.open_time ?? payload.timestamp,
  );
  return (
    currentOpenedAt !== null &&
    incomingOpenedAt !== null &&
    incomingOpenedAt > currentOpenedAt
  );
};

export interface OptimisticPositionIntent {
  clientOrderId: string;
  brokerOrderId?: string;
  accountName: string;
  brokerAccountId: string | number;
  externalAccountId: string | number;
  symbol: string;
  side: "long" | "short";
  quantity: number;
  state: "submitting" | "awaiting_fill";
}

// F1-A: the SIG-345-correct container walker now lives in ONE shared module —
// features/positions/utils/position-list-container — used by this delta path
// and by positions.service's optimistic close paints.

/** Mark open broker truth as closing without claiming a fill occurred. */
export const markOpenPositionsClosing = (queryClient: QueryClient): void => {
  markPositionsClosingForAccounts(queryClient, null);
};

/**
 * Flatten-click paint: mark matching open rows "closing" immediately so the
 * book does not sit live until the 10–60s flatten HTTP returns. `accountKeys`
 * null = every open row (emergency / unknown group membership).
 */
export const markPositionsClosingForAccounts = (
  queryClient: QueryClient,
  accountKeys: ReadonlySet<string> | null,
): void => {
  rebuildPositionCaches(queryClient, (position) => {
    if (
      !["active", "open"].includes(String(position?.status ?? "").toLowerCase())
    ) {
      return position;
    }
    if (accountKeys && accountKeys.size > 0) {
      const candidates = [
        position.account,
        position.account_id,
        position.broker_account_id,
        position.brokerAccountId,
      ]
        .filter((value) => value != null && value !== "")
        .map(String);
      if (!candidates.some((key) => accountKeys.has(key))) {
        return position;
      }
    }
    return {
      ...position,
      status: "closing",
      execution_state: "liquidating",
      optimistic_emergency: true,
    };
  });
};

/**
 * Per-account drain for the flatten wave: rows an account-level broker proof
 * settled leave the table now, not when the whole wave's terminal frame
 * lands. `accountKey` null = every liquidating row (recovery pass).
 */
const closeLiquidatingRowsForAccount = (
  queryClient: QueryClient,
  accountKey: unknown,
): boolean => {
  const expected = accountKey == null || accountKey === "" ? null : String(accountKey);
  let touched = false;
  rebuildPositionCaches(queryClient, (position) => {
    if (String(position?.execution_state ?? "") !== "liquidating") {
      return position;
    }
    if (expected != null) {
      const candidates = [
        position.account,
        position.account_id,
        position.broker_account_id,
        position.brokerAccountId,
      ]
        .filter((value) => value != null && value !== "")
        .map(String);
      if (!candidates.includes(expected)) {
        return position;
      }
    }
    touched = true;
    const {
      execution_state: _executionState,
      optimistic_emergency: _optimisticEmergency,
      ...rest
    } = position;
    return { ...rest, status: "closed", quantity: 0 };
  });
  return touched;
};

const extractPositionEvent = (
  message: AnyRecord,
): { action: string; payload: AnyRecord } | null => {
  const type: string = String(message?.type || message?.event_type || "");
  const payload: AnyRecord | undefined = message?.payload ?? message?.data;
  if (
    !type.startsWith("position.") ||
    !payload ||
    payload.position_id == null
  ) {
    return null;
  }
  return { action: type.slice("position.".length), payload };
};

const BROKER_QUOTE_FIELDS = new Set([
  "position_id",
  "action",
  "quantity",
  "broker_position_id",
  "account_id",
  "broker_account_id",
  "symbol",
  "source",
  "sync_source",
  "lifecycle_key",
  "observed_seq",
  "mark_source",
  "mark_state",
  "mark_measured_at",
  "observed_at",
  "mark_age_seconds",
  "anchor_at",
  "current_price",
  "quote_price",
  "unrealized_pnl",
  "pnl",
  "__broker_quote_flush",
]);

const isBrokerQuoteOnly = (action: string, payload: AnyRecord): boolean =>
  action === "updated" &&
  payload.mark_source === "broker_quote" &&
  Object.keys(payload).every((key) => BROKER_QUOTE_FIELDS.has(key));

type BrokerQuoteBatch = {
  frames: Map<string, AnyRecord>;
  scheduled: boolean;
};

const brokerQuoteBatches = new WeakMap<object, BrokerQuoteBatch>();

const scheduleBrokerQuote = (
  queryClient: QueryClient,
  payload: AnyRecord,
): void => {
  let batch = brokerQuoteBatches.get(queryClient);
  if (!batch) {
    batch = { frames: new Map(), scheduled: false };
    brokerQuoteBatches.set(queryClient, batch);
  }
  const key = closedIdentity(payload.position_id, payload.lifecycle_key);
  if (!key) return;
  const previous = batch.frames.get(key);
  const incomingSeq = asObservedSeq(payload.observed_seq);
  const previousSeq = previous ? asObservedSeq(previous.observed_seq) : null;
  if (
    incomingSeq !== null &&
    previousSeq !== null &&
    (compareObservedSeq(incomingSeq, previousSeq) ?? 1) <= 0
  ) {
    return;
  }
  batch.frames.set(key, previous ? { ...previous, ...payload } : payload);
  if (batch.scheduled) return;
  batch.scheduled = true;
  const flush = () => {
    batch!.scheduled = false;
    const frames = [...batch!.frames.values()];
    batch!.frames.clear();
    for (const frame of frames) {
      applyPositionsChannelMessage(
        { type: "position.updated", payload: { ...frame, __broker_quote_flush: true } },
        queryClient,
      );
    }
  };
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(flush);
  } else {
    setTimeout(flush, 0);
  }
};

/**
 * SIG-688 mirror frames (ledger item 12, F1): `_mirror_entities_to_stream`
 * publishes every broker position entity as `position.updated` with
 * `{ source: "broker_entity", account_id, position: <raw Tradovate entity> }`
 * — no top-level `position_id`, so `extractPositionEvent` rejected every one
 * and the mirror only ever bought an immediate REST refetch. The raw entity
 * DOES carry the join key: backend stores `Position.position_id =
 * str(entity.id)` (user_sync_socket `_apply_position_entities`), which both
 * REST and WS-built rows expose as `broker_position_id`.
 */
const extractBrokerEntityPosition = (message: AnyRecord): AnyRecord | null => {
  const type: string = String(message?.type || message?.event_type || "");
  const payload: AnyRecord | undefined = message?.payload ?? message?.data;
  if (!type.startsWith("position.") || payload?.source !== "broker_entity") {
    return null;
  }
  const entity = payload.position;
  if (!entity || typeof entity !== "object" || entity.id == null) return null;
  return entity as AnyRecord;
};

/**
 * F3 resurrection guard: the production diff publisher sends NO status field
 * on position.updated, so a status gate alone cannot catch the real failure
 * class — a stale `updated` (qty>0) delivered AFTER `closed` for the same id
 * would re-dispatch as opened and resurrect the row as active. Remember ids
 * we closed recently (per QueryClient; broker mirror flats count too — the
 * backend stores Position.position_id = str(entity.id)) and refuse the
 * opened re-dispatch for them.
 */
const RECENTLY_CLOSED_TTL_MS = 10_000;
const recentlyClosedByClient = new WeakMap<object, Map<string, number>>();

const closedIdentity = (id: unknown, lifecycleKey?: unknown): string | null => {
  if (id == null) return null;
  const lifecycle = asObservedSeq(lifecycleKey);
  return lifecycle ? `${String(id)}:${lifecycle}` : String(id);
};

const noteRecentlyClosed = (client: QueryClient, id: unknown, lifecycleKey?: unknown): void => {
  if (id == null) return;
  let closedAt = recentlyClosedByClient.get(client);
  if (!closedAt) {
    closedAt = new Map();
    recentlyClosedByClient.set(client, closedAt);
  }
  const now = Date.now();
  const identity = closedIdentity(id, lifecycleKey);
  if (!identity) return;
  closedAt.set(identity, now);
  const bareId = String(id);
  if (identity !== bareId) closedAt.set(bareId, now);
  for (const [key, at] of closedAt) {
    if (now - at > RECENTLY_CLOSED_TTL_MS) closedAt.delete(key);
  }
};

const wasRecentlyClosed = (client: QueryClient, id: unknown, lifecycleKey?: unknown): boolean => {
  const identity = closedIdentity(id, lifecycleKey);
  if (!identity) return false;
  const closedAt = recentlyClosedByClient.get(client);
  const at =
    closedAt?.get(identity) ??
    (asObservedSeq(lifecycleKey) === null ? closedAt?.get(String(id)) : undefined);
  return at != null && Date.now() - at <= RECENTLY_CLOSED_TTL_MS;
};

function isInstantCloseRow(row: AnyRecord, payload: AnyRecord): boolean {
  if (row?.instant_close === true || payload?.instant_close === true) return true;
  const hold = payload?.hold_ms;
  if (typeof hold === "number" && Number.isFinite(hold)) {
    return isInstantClose(null, null, hold);
  }
  const opened = row?.opened_at ?? row?.open_time;
  const closed = payload?.closed_at ?? payload?.timestamp;
  if (isInstantClose(opened, closed)) return true;
  if (opened && !closed) {
    return isInstantClose(opened, new Date().toISOString());
  }
  return false;
}

/** Open-only lists drop a closed row; mixed lists keep it marked closed.
 * Instant-close rows stay visible on the open table so the anomaly is seen. */
const closeRowInList = (
  rows: AnyRecord[],
  idx: number,
  payload: AnyRecord,
): AnyRecord[] => {
  const instant = isInstantCloseRow(rows[idx], payload);
  const closedRow = {
    ...mergeDelta(rows[idx], payload, { closing: true }),
    status: "closed",
    instant_close: instant,
  };
  const keepsClosed = rows.some((p) =>
    ["closed", "cancelled"].includes(String(p?.status ?? "").toLowerCase()),
  );
  return keepsClosed || instant
    ? rows.map((p, i) => (i === idx ? closedRow : p))
    : rows.filter((_, i) => i !== idx);
};

/**
 * Apply a broker_entity mirror frame to rows we already hold, matched
 * STRICTLY on broker_position_id === entity.id (fail-closed: a fuzzy
 * contract/account match could close the wrong row). netPos carries both
 * quantity and direction; netPos === 0 is the broker saying flat → closed.
 * Unmatched open entities insert when sign-complete, else absorb onto the
 * trailing 1.5s reconcile — a per-entity REST refetch coalesces onto a
 * partial book (R3 74s fan-out).
 */
const inferSymbolFromRows = (
  rows: AnyRecord[],
  entity: AnyRecord,
): string | null => {
  const fromEntity = String(
    entity.symbol ?? entity.contractName ?? entity.name ?? "",
  ).trim();
  if (fromEntity) return fromEntity;
  const contractId = entity.contractId;
  if (contractId == null || contractId === "") return null;
  const sibling = rows.find(
    (row) =>
      String(row?.broker_contract?.contract_id ?? row?.contract_id ?? "") ===
      String(contractId),
  );
  const siblingSymbol = String(
    sibling?.instrument ?? sibling?.contract_symbol ?? sibling?.symbol ?? "",
  ).trim();
  return siblingSymbol || null;
};

const buildBrokerEntityRow = (
  entity: AnyRecord,
  payload: AnyRecord,
  existingRows: AnyRecord[],
  netPos: number,
): AnyRecord | null => {
  if (!Number.isFinite(netPos) || netPos === 0) return null;
  const direction = netPos > 0 ? "long" : "short";
  const quantity = Math.abs(netPos);
  const symbol = inferSymbolFromRows(existingRows, entity);
  const accountId = payload?.account_id ?? entity.accountId;
  if (!symbol || accountId == null) return null;
  const brokerPositionId = String(entity.id);
  const rawPrice = entity.netPrice ?? entity.price;
  const price = Number(rawPrice);
  const hasPrice = Number.isFinite(price) && price > 0 && price < 1e7;

  const row: AnyRecord = {
    id: brokerPositionId,
    broker_position_id: brokerPositionId,
    account: String(accountId),
    broker_account_id: String(accountId),
    broker_metadata: {
      accountId: entity.accountId ?? accountId,
      contractId: entity.contractId,
    },
    instrument: symbol,
    contract_symbol: symbol,
    direction,
    quantity,
    volume: String(quantity),
    open_volume: String(quantity),
    open_price: hasPrice ? String(price) : null,
    current_price: null,
    quote_price: null,
    mark_state: "unknown",
    pnl: null,
    unrealized_pnl: null,
    status: "active",
    opened_at:
      payload?.opened_at ??
      payload?.open_time ??
      entity.timestamp ??
      entity.opened_at ??
      null,
    open_time:
      payload?.open_time ??
      payload?.opened_at ??
      entity.timestamp ??
      entity.opened_at ??
      null,
  };
  if (entity.contractId != null) {
    row.broker_contract = {
      contract_id: entity.contractId,
      contract_symbol: symbol,
    };
  }
  return isSignCompletePosition(row) ? row : null;
};

const applyBrokerEntityPosition = (
  entity: AnyRecord,
  queryClient: QueryClient,
  payload: AnyRecord = {},
): boolean => {
  // `Number(null)` and `Number("")` are BOTH 0 — a mirror entity with a
  // missing/empty netPos must be unhandled (caller refetches), never read as
  // the broker declaring flat (which would close a live row).
  const rawNetPos = entity.netPos;
  const netPos =
    typeof rawNetPos === "number"
      ? rawNetPos
      : typeof rawNetPos === "string" && rawNetPos.trim() !== ""
        ? Number(rawNetPos)
        : NaN;
  if (!Number.isFinite(netPos)) return false;
  // Flat carries explicit zeros: a mixed cache keeps the closed row, and its
  // stale quantity/unrealized P&L must not survive a genuine broker flat.
  const delta: AnyRecord =
    netPos === 0
      ? { quantity: 0, unrealized_pnl: 0 }
      : { quantity: Math.abs(netPos), side: netPos > 0 ? "long" : "short" };
  // Raw mirrors carry structural facts only. Their optional unversioned
  // openPnl is not the persisted canonical observation.
  if (netPos !== 0) {
    // Tradovate position.netPrice is average ENTRY (boughtValue/bought), not a
    // mark. Only the canonical contract may deliver a current price.
    const rawNetPrice = entity.netPrice;
    if (rawNetPrice != null && String(rawNetPrice).trim() !== "") {
      const entry = Number(rawNetPrice);
      if (Number.isFinite(entry) && entry > 0 && entry < 1e7) {
        delta.entry_price = entry;
      }
    }
  }
  if (netPos === 0) noteRecentlyClosed(queryClient, entity.id, payload.lifecycle_key);
  const noteClosedRowIds = (row: AnyRecord | undefined): void => {
    // The F3 guard checks the LOCAL payload.position_id — record the matched
    // row's local ids too, or a broker-id-only entry never joins and a stale
    // status-less update resurrects the row (round-3 review P1).
    noteRecentlyClosed(queryClient, row?.id, row?.lifecycle_key);
    noteRecentlyClosed(queryClient, row?.position_id, row?.lifecycle_key);
  };

  let touched = false;
  queryClient.setQueriesData(
    { predicate: (query) => (query.queryKey[0] === "positions" || (query.queryKey[0] === "profile-positions" && query.state.data !== undefined)) },
    (oldData: AnyRecord | undefined) => {
      const container = getPositionListContainer(oldData);
      if (!container) return oldData;
      const rows = container.positions;
      const idx = rows.findIndex(
        (p) =>
          p?.broker_position_id != null &&
          String(p.broker_position_id) === String(entity.id) &&
          (entity.accountId == null || p.broker_metadata?.accountId == null ||
            String(p.broker_metadata.accountId) === String(entity.accountId)),
      );
      if (idx === -1) {
        if (netPos === 0) return oldData;
        if (!openedAccountIsKnownToMember(queryClient, {
          account_id: payload?.account_id ?? entity.accountId,
          broker_account_id: entity.accountId ?? payload?.account_id,
        })) {
          return oldData;
        }
        const inserted = buildBrokerEntityRow(entity, payload, rows, netPos);
        if (!inserted) return oldData;
        touched = true;
        const rebuilt = container.rebuild([inserted, ...rows]);
        if (Array.isArray(rebuilt)) return rebuilt;
        return {
          ...rebuilt,
          ...(typeof oldData?.total === "number"
            ? { total: oldData.total + 1 }
            : {}),
          ...(typeof oldData?.count === "number"
            ? { count: oldData.count + 1 }
            : {}),
        };
      }
      touched = true;
      if (netPos === 0) {
        noteClosedRowIds(rows[idx]);
        return container.rebuild(closeRowInList(rows, idx, delta));
      }
      return container.rebuild(
        rows.map((p, i) => (i === idx ? mergeDelta(p, delta) : p)),
      );
    },
  );
  // Sign-complete unmatched opens insert above (touched=true). Never claim
  // applied unless a row landed in the cache the UI select reads. Unsigned
  // unmatched opens return false; the handler trailing-reconciles once.
  return touched;
};

const mergeDelta = (
  row: AnyRecord,
  payload: AnyRecord,
  options: { closing?: boolean } = {},
): AnyRecord => {
  if (!samePositionScope(row, payload) || rejectsLifecycleDelta(row, payload)) return row;
  const next: AnyRecord = { ...row };
  const incomingAnchorAt = Date.parse(String(payload.anchor_at ?? ""));
  const previousAnchorAt = Date.parse(String(row.anchor_at ?? ""));
  const incomingReference = Number(payload.anchor_reference_price);
  const hasPricedMark = row.pnl != null && Number.isFinite(Number(row.pnl)) && row.mark_source != null;
  const repeatsAnchor = Number.isFinite(incomingAnchorAt) && Number.isFinite(previousAnchorAt) && incomingAnchorAt <= previousAnchorAt;
  const newerPricedAnchor = Number.isFinite(incomingAnchorAt) &&
    (!Number.isFinite(previousAnchorAt) || incomingAnchorAt > previousAnchorAt) &&
    Number.isFinite(incomingReference) && incomingReference > 0;
  // A fill mirror repeats stored P&L; its new event timestamp is not a new
  // measurement. Reusing it as a fresh anchor resets losses back to old profit.
  const lifecycleMark = payload.pnl_mark_pending === true || payload.mark_source === "broker_last_good" ||
    (payload.sync_source === "fill_ingest" && payload.mark_source == null);
  const previousQty = Number(row.quantity ?? row.volume ?? row.open_volume);
  const incomingQty = payload.quantity == null ? Number.NaN : Number(payload.quantity);
  const qtyChanged =
    Number.isFinite(incomingQty) && Number.isFinite(previousQty) && incomingQty !== previousQty;
  const previousSide = String(row.direction ?? "").toLowerCase();
  const incomingSide = String(payload.side ?? "").toLowerCase();
  const sideChanged = incomingSide !== "" && previousSide !== "" && incomingSide !== previousSide;
  const incomingEntryRaw = payload.entry_price ?? payload.open_price;
  const incomingEntry =
    incomingEntryRaw == null || String(incomingEntryRaw).trim() === ""
      ? Number.NaN
      : Number(incomingEntryRaw);
  const hasIncomingEntry =
    Number.isFinite(incomingEntry) && incomingEntry > 0 && incomingEntry < 1e7;
  const previousEntry = Number(row.open_price);
  const entryChanged =
    hasIncomingEntry && Number.isFinite(previousEntry) && incomingEntry !== previousEntry;
  const exposureChanged = qtyChanged || sideChanged || entryChanged;
  const preserveMark = !options.closing && hasPricedMark && !exposureChanged &&
    (repeatsAnchor || (lifecycleMark && !newerPricedAnchor));
  if (payload.quantity != null) {
    next.quantity = payload.quantity;
    next.volume = String(payload.quantity);
  }
  if (hasIncomingEntry) {
    next.open_price = String(incomingEntry);
  }
  if (
    !preserveMark &&
    payload.current_price != null &&
    !(lifecycleMark && !newerPricedAnchor)
  ) {
    next.current_price = String(payload.current_price);
    next.quote_price = String(payload.current_price);
  }
  if (!preserveMark && hasOwn(payload, "unrealized_pnl")) {
    // Broker position deltas frequently carry unrealized_pnl=0 because
    // Tradovate's openPnl is missing/0 right after a fill — while the live
    // quote pipeline has already marked the row to market. A broker ZERO must
    // not stomp a non-zero live mark (the row would render a known-flat
    // "$0.00" until the next tick). Non-zero broker P&L is authoritative,
    // and so is ANY value on a closing row — a closed position genuinely has
    // zero open exposure, so the guard must not preserve a stale live mark
    // there (Greptile 2026-07-29 review note).
    const incoming = payload.unrealized_pnl == null ? null : Number(payload.unrealized_pnl);
    const existing = Number(row.pnl);
    // A19 r2 fix 2: a zero from the cash-exit watcher's account-level
    // snapshot bracket is a MEASURED broker value (Tradovate itself says the
    // account's dollar-open is $0) — it must win over any client estimate.
    // Entity-frame zeros stay guarded: Tradovate position entities carry a
    // transient missing/0 openPnl right after a fill (the original stomp).
    const measuredAnchorZero =
      payload.sync_source === "cash_exit_watcher_anchor" ||
      payload.mark_source === "broker_anchor" ||
      payload.mark_source === "broker_quote";
    const zeroStompsLiveMark =
      !options.closing &&
      incoming === 0 &&
      Number.isFinite(existing) &&
      existing !== 0 &&
      !measuredAnchorZero;
    if (!zeroStompsLiveMark) {
      if (Number.isFinite(incomingAnchorAt)) next.anchor_at = payload.anchor_at;
      next.unrealized_pnl = Number.isFinite(incoming) ? incoming : null;
      next.pnl = Number.isFinite(incoming) ? String(incoming) : null;
      // DL-55: the provenance label travels WITH the value — a discarded
      // broker zero must not relabel a surviving tape mark as broker truth,
      // and (codex r3 f7) a TRANSIENT broker 0 over a tape-priced $0.00 must
      // not erase the degraded disclosure either: broker zeros are only
      // authoritative labels on closing rows, on measured snapshot zeros,
      // or when the row was not tape-labeled.
      const zeroRelabelsTape =
        !options.closing &&
        incoming === 0 &&
        !measuredAnchorZero &&
        row.mark_source === "tape_degraded";
      const markSource = asBrokerMarkSource(payload.mark_source);
      if (markSource !== null && !zeroRelabelsTape) {
        next.mark_source = markSource;
        // A19 r3 fix 4: an anchor frame that carries its OWN reference price
        // (the tape mid AT stamp time) seeds the projection basis directly —
        // pairing the anchor with the first LATER quote instead skewed the
        // basis by the move between stamp and quote ($0@100 + quote@105
        // rendered $0@105, losing the move until the next anchor).
        const referencePrice = Number(payload.anchor_reference_price);
        if (
          ["broker_anchor", "broker_last_good"].includes(markSource) &&
          Number.isFinite(incoming) &&
          Number.isFinite(referencePrice) &&
          referencePrice > 0
        ) {
          next.anchor_basis_pnl = incoming;
          next.anchor_basis_mid = referencePrice;
        } else {
          // A server-sent P&L without its own reference invalidates any
          // stored basis: keeping it let the NEXT quote tick project off a
          // stale pair and overwrite this fresh broker number (the
          // +$99-vs-broker-+$45 drift class, 2026-08-18).
          next.anchor_basis_pnl = undefined;
          next.anchor_basis_mid = undefined;
        }
      } else if (markSource === null) {
        // Same rule for unlabeled frames (fill-ingest mirror): new P&L,
        // no reference — the old basis no longer describes this value.
        next.anchor_basis_pnl = undefined;
        next.anchor_basis_mid = undefined;
        // A basis-less "broker_anchor_projected" row would fall through the
        // projection to the tape path and lose this broker value — demote
        // to broker_last_good so the next quote tick RE-CAPTURES a basis
        // from this fresh value instead.
        if (hasOwn(payload, "mark_source")) {
          next.mark_source = null;
        } else if (String(next.mark_source ?? "") === "broker_anchor_projected") {
          next.mark_source = "broker_last_good";
        }
      }
    }
  }
  if (payload.realized_pnl != null) next.realized_pnl = payload.realized_pnl;
  if (payload.side) next.direction = payload.side;
  if (hasOwn(payload, "lifecycle_key")) next.lifecycle_key = asObservedSeq(payload.lifecycle_key);
  if (hasOwn(payload, "observed_seq")) next.observed_seq = asObservedSeq(payload.observed_seq);
  if (hasOwn(payload, "mark_measured_at")) {
    next.mark_measured_at =
      typeof payload.mark_measured_at === "string" &&
      Number.isFinite(Date.parse(payload.mark_measured_at))
        ? payload.mark_measured_at
        : null;
  }
  if (hasOwn(payload, "mark_age_seconds")) {
    const age = payload.mark_age_seconds == null ? NaN : Number(payload.mark_age_seconds);
    next.mark_age_seconds = Number.isFinite(age) && age >= 0 ? age : null;
  }
  if (hasOwn(payload, "mark_source") && payload.mark_source == null) {
    next.mark_source = null;
  }
  // A priced basis belongs to that qty/side. Tradovate updates netPos on the
  // same entity id; keeping the old dollar/mid pair after a scale or reverse
  // projects a different exposure. Unproven replacement must not look like a
  // fresh broker mark — a later priced anchor may re-seed.
  if (exposureChanged && !newerPricedAnchor && !options.closing) {
    next.anchor_basis_pnl = undefined;
    next.anchor_basis_mid = undefined;
    next.pnl = null;
    next.unrealized_pnl = null;
    const source = String(next.mark_source ?? "");
    if (source === "broker_anchor" || source === "broker_anchor_projected") {
      next.mark_source = "broker_last_good";
    }
    if ((qtyChanged || sideChanged) && !hasIncomingEntry) {
      next.open_price = null;
    }
  }
  if (hasOwn(payload, "mark_state")) {
    const raw = hasOwn(payload, "unrealized_pnl") ? payload.unrealized_pnl : payload.pnl;
    const value = finiteNumber(raw);
    const known = payload.mark_state === "known" && value !== null;
    next.mark_state = known ? "known" : "unknown";
    next.pnl = known ? String(raw) : null;
    next.unrealized_pnl = known ? value : null;
    next.mark_source = known ? asBrokerMarkSource(payload.mark_source) : null;
    next.mark_measured_at = parseMeasuredAt(payload.mark_measured_at) === null ? null : payload.mark_measured_at;
    const age = finiteNumber(payload.mark_age_seconds);
    next.mark_age_seconds = age !== null && age >= 0 ? age : null;
    next.observed_at = payload.observed_at ?? null;
    next.anchor_basis_pnl = undefined;
    next.anchor_basis_mid = undefined;
  } else if (exposureChanged && !options.closing) {
    next.mark_state = "unknown";
  }
  if (hasOwn(payload, "observed_at")) next.observed_at = payload.observed_at;
  // Current and protection are independent facts; explicit null clears them.
  for (const key of ["current_price", "quote_price", "stop_loss", "take_profit"]) {
    if (hasOwn(payload, key) &&
        (!["current_price", "quote_price"].includes(key) || payload[key] == null || hasOwn(payload, "mark_state"))) {
      const value = finiteNumber(payload[key]);
      next[key] = value === null ? null : String(value);
    }
  }
  if (hasOwn(payload, "current_price") && !hasOwn(payload, "quote_price")) {
    next.quote_price = next.current_price;
  }
  return next;
};

const filterValues = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const matchesFilter = (filter: unknown, actual: unknown): boolean => {
  const expected = filterValues(filter);
  return expected.length === 0 || expected.includes(String(actual ?? ""));
};

const collectKnownAccountIds = (queryClient: QueryClient): Set<string> | null => {
  const getter = (
    queryClient as QueryClient & {
      getQueriesData?: (filters: { queryKey: unknown[] }) => Array<[unknown, unknown]>;
    }
  ).getQueriesData;
  if (typeof getter !== "function") return null;
  let queries: Array<[unknown, unknown]>;
  try {
    queries = getter.call(queryClient, { queryKey: ["accounts"] });
  } catch {
    return null;
  }
  if (!Array.isArray(queries) || queries.length === 0) return null;
  const ids = new Set<string>();
  let sawData = false;
  for (const [, data] of queries) {
    if (data == null) continue;
    sawData = true;
    const root = data as AnyRecord;
    const bags: unknown[] = [];
    if (Array.isArray(root.items)) bags.push(...root.items);
    if (Array.isArray(root.accounts)) bags.push(...root.accounts);
    const results = root.results as AnyRecord | undefined;
    if (results && Array.isArray(results.accounts)) bags.push(...results.accounts);
    for (const row of bags) {
      const acct = row as AnyRecord | null;
      if (!acct) continue;
      for (const value of [acct.id, acct.account_id, acct.accountId]) {
        if (value != null && value !== "") ids.add(String(value));
      }
    }
  }
  return sawData ? ids : null;
};

const openedAccountIsKnownToMember = (
  queryClient: QueryClient,
  payload: AnyRecord,
): boolean => {
  const known = collectKnownAccountIds(queryClient);
  if (known == null) return true;
  const candidates = [payload.account_id, payload.broker_account_id]
    .filter((value) => value != null && value !== "")
    .map(String);
  if (candidates.length === 0) return false;
  return candidates.some((id) => known.has(id));
};

const openedEventMatchesQuery = (query: Query, payload: AnyRecord): boolean => {
  const queryKey = query.queryKey;
  if (queryKey[0] !== "positions" || queryKey[1] === "history") return false;
  if (queryKey.length === 1) return true;
  if (typeof queryKey[1] !== "string") return false;

  let params: AnyRecord;
  try {
    params = JSON.parse(queryKey[1]) as AnyRecord;
  } catch {
    return false;
  }

  const status = String(params.status ?? "active").toLowerCase();
  if (!["active", "open", "all"].includes(status)) return false;
  if (Number(params.page ?? 1) > 1 || Number(params.offset ?? 0) > 0)
    return false;
  if (params.from_date || params.to_date || params.broker) return false;

  return (
    matchesFilter(params.account_pk, payload.account_id) &&
    matchesFilter(params.account_pks, payload.account_id) &&
    matchesFilter(params.account_id, payload.broker_account_id) &&
    matchesFilter(params.account_ids, payload.broker_account_id) &&
    matchesFilter(params.instrument, payload.symbol) &&
    matchesFilter(params.instruments, payload.symbol) &&
    matchesFilter(params.direction, payload.side) &&
    matchesFilter(params.environment, payload.environment)
  );
};

const buildOpenedPosition = (payload: AnyRecord): AnyRecord | null => {
  const symbol = String(payload.symbol ?? "").trim();
  const direction = String(payload.side ?? "").toLowerCase();
  const quantity = Number(payload.quantity);
  const entryPrice = Number(payload.entry_price);
  const localAccountId = payload.account_id;
  // Structural minimum only (SIG-1216 perceived-start): a row needs symbol,
  // side, size, and account to EXIST on screen. Prices may lag the open frame
  // — those cells render an honest "—" (never an invented figure) and the
  // trailing reconciliation fills the real numbers ~1.5s later. Gating the
  // whole insert on entry_price made the row itself wait on the refetch.
  if (
    !symbol ||
    !["long", "short"].includes(direction) ||
    !Number.isFinite(quantity) ||
    quantity <= 0 ||
    localAccountId == null
  ) {
    return null;
  }
  const hasEntry = Number.isFinite(entryPrice) && entryPrice > 0;

  const currentPrice = payload.current_price;
  const rawUnrealizedPnl = payload.unrealized_pnl;
  const parsedUnrealizedPnl = finiteNumber(rawUnrealizedPnl);
  const unrealizedPnl = payload.mark_state === "unknown" ? null : parsedUnrealizedPnl;
  return {
    id: payload.position_id,
    account: String(payload.broker_account_id ?? localAccountId),
    broker: payload.broker_name ?? "",
    broker_name: payload.broker_name ?? null,
    broker_user_id: payload.broker_user_id,
    account_id: localAccountId,
    provider_account_id: payload.broker_account_id == null ? null : String(payload.broker_account_id),
    broker_position_id:
      payload.broker_position_id == null
        ? undefined
        : String(payload.broker_position_id),
    broker_account_id: String(localAccountId),
    broker_metadata: {
      accountId: payload.broker_account_id,
      sync_source: payload.sync_source,
    },
    instrument: symbol,
    contract_symbol: symbol,
    direction,
    quantity,
    volume: String(quantity),
    open_volume: String(quantity),
    open_price: hasEntry ? String(payload.entry_price) : null,
    current_price: currentPrice == null ? null : String(currentPrice),
    quote_price: currentPrice == null ? null : String(currentPrice),
    pnl: unrealizedPnl == null ? null : String(unrealizedPnl),
    unrealized_pnl: unrealizedPnl,
    realized_pnl: payload.realized_pnl,
    status: "active",
    environment: payload.environment,
    opened_at: payload.opened_at ?? payload.open_time ?? payload.timestamp ?? null,
    open_time: payload.open_time ?? payload.opened_at ?? payload.timestamp ?? null,
    last_update_time:
      payload.last_update_time ??
      payload.opened_at ??
      payload.open_time ??
      payload.timestamp ??
      null,
    mark_state: payload.mark_state === "known" && unrealizedPnl !== null ? "known" : "unknown",
    observed_at: payload.observed_at ?? null,
    stop_loss: payload.stop_loss ?? null,
    take_profit: payload.take_profit ?? null,
    lifecycle_key: asObservedSeq(payload.lifecycle_key),
    observed_seq: asObservedSeq(payload.observed_seq),
    mark_measured_at:
      typeof payload.mark_measured_at === "string" &&
      Number.isFinite(Date.parse(payload.mark_measured_at))
        ? payload.mark_measured_at
        : null,
    mark_source: asBrokerMarkSource(payload.mark_source),
    mark_age_seconds:
      payload.mark_age_seconds != null && Number.isFinite(Number(payload.mark_age_seconds)) && Number(payload.mark_age_seconds) >= 0
        ? Number(payload.mark_age_seconds)
        : null,
  };
};

const optimisticPositionId = (clientOrderId: string): number => {
  let hash = 0;
  for (let index = 0; index < clientOrderId.length; index += 1) {
    hash = (hash * 31 + clientOrderId.charCodeAt(index)) | 0;
  }
  return -(Math.abs(hash) || 1);
};

const optimisticMatchesOpened = (
  position: AnyRecord,
  payload: AnyRecord,
): boolean => {
  if (!position?.optimistic) return false;
  const brokerEventIds = [
    payload.broker_order_id,
    payload.order_id,
    payload.broker_position_id,
  ]
    .filter((value) => value != null)
    .map(String);
  if (
    position.optimistic_order_id != null &&
    brokerEventIds.includes(String(position.optimistic_order_id))
  ) {
    return true;
  }
  return (
    String(position.broker_account_id ?? "") ===
      String(payload.account_id ?? "") &&
    String(position.instrument ?? "").toUpperCase() ===
      String(payload.symbol ?? "").toUpperCase() &&
    String(position.direction ?? "").toLowerCase() ===
      String(payload.side ?? "").toLowerCase()
  );
};

export const insertOptimisticPosition = (
  queryClient: QueryClient,
  intent: OptimisticPositionIntent,
): void => {
  const payload = {
    account_id: intent.brokerAccountId,
    broker_account_id: intent.externalAccountId,
    symbol: intent.symbol,
    side: intent.side,
  };
  const pending = {
    id: optimisticPositionId(intent.clientOrderId),
    account: intent.accountName,
    broker: "",
    broker_name: "Tradovate",
    broker_account_id: String(intent.brokerAccountId),
    broker_metadata: { accountId: intent.brokerAccountId },
    instrument: intent.symbol,
    contract_symbol: intent.symbol,
    direction: intent.side,
    quantity: intent.quantity,
    volume: String(intent.quantity),
    open_volume: String(intent.quantity),
    open_price: "",
    current_price: null,
    quote_price: null,
    pnl: null,
    // A19 r3 fix 3: both P&L fields in lockstep on every writer.
    unrealized_pnl: null,
    status: "pending",
    execution_state: intent.state,
    optimistic: true,
    optimistic_client_order_id: intent.clientOrderId,
    optimistic_order_id: intent.brokerOrderId,
    opened_at: new Date().toISOString(),
  };

  queryClient.setQueriesData(
    {
      queryKey: ["positions"],
      predicate: (query) => openedEventMatchesQuery(query, payload),
    },
    (oldData: AnyRecord | undefined) => {
      const container = getPositionListContainer(oldData);
      if (!container) return oldData;
      const existing = container.positions.findIndex(
        (position) =>
          position?.optimistic_client_order_id === intent.clientOrderId,
      );
      const next =
        existing >= 0
          ? container.positions.map((position, index) =>
              index === existing ? { ...position, ...pending } : position,
            )
          : [pending, ...container.positions];
      const rebuilt = container.rebuild(next);
      if (existing >= 0) return rebuilt;
      return {
        ...rebuilt,
        ...(typeof oldData?.total === "number"
          ? { total: oldData.total + 1 }
          : {}),
        ...(typeof oldData?.count === "number"
          ? { count: oldData.count + 1 }
          : {}),
      };
    },
  );
};

const extractSnapshotRows = (message: AnyRecord): AnyRecord[] | null => {
  const type = String(message?.type || message?.event_type || "");
  if (type !== "positions.initial") return null;
  const payload = message?.payload ?? message?.data;
  return Array.isArray(payload) ? payload : null;
};

const normalizeSnapshotRow = (raw: AnyRecord): AnyRecord | null => {
  if (!raw || typeof raw !== "object") return null;
  const direction = readDirection(raw.direction ?? raw.side);
  const symbol = String(
    raw.instrument ?? raw.symbol ?? raw.contract_symbol ?? "",
  ).trim();
  const quantity = Number(raw.quantity ?? raw.volume ?? raw.open_volume);
  if (
    !isKnownDirection(direction) ||
    !symbol ||
    !Number.isFinite(quantity) ||
    quantity <= 0
  ) {
    return null;
  }
  const accountId = raw.broker_account_id ?? raw.account_id;
  const pnlRaw = raw.unrealized_pnl ?? raw.pnl;
  const pnlNum = finiteNumber(pnlRaw);
  const hasPnl = raw.mark_state !== "unknown" && pnlNum !== null;
  const row: AnyRecord = {
    ...raw,
    id: raw.id ?? raw.position_id,
    broker_position_id:
      raw.broker_position_id != null
        ? String(raw.broker_position_id)
        : raw.position_id != null
          ? String(raw.position_id)
          : undefined,
    account: raw.account ?? (accountId != null ? String(accountId) : ""),
    broker_account_id: accountId != null ? String(accountId) : raw.broker_account_id,
    instrument: symbol,
    contract_symbol: raw.contract_symbol ?? symbol,
    direction,
    quantity,
    volume: String(raw.volume ?? quantity),
    open_volume: String(raw.open_volume ?? quantity),
    status:
      String(raw.status ?? "").toLowerCase() === "open"
        ? "active"
        : (raw.status ?? "active"),
    pnl: hasPnl ? String(pnlNum) : null,
    unrealized_pnl: hasPnl ? pnlNum : null,
    mark_state: raw.mark_state === "known" && hasPnl ? "known" : "unknown",
    lifecycle_key: asObservedSeq(raw.lifecycle_key),
    observed_seq: asObservedSeq(raw.observed_seq),
    mark_measured_at:
      typeof raw.mark_measured_at === "string" &&
      Number.isFinite(Date.parse(raw.mark_measured_at))
        ? raw.mark_measured_at
        : null,
    mark_source: asBrokerMarkSource(raw.mark_source),
    mark_age_seconds:
      raw.mark_age_seconds != null && Number.isFinite(Number(raw.mark_age_seconds)) && Number(raw.mark_age_seconds) >= 0
        ? Number(raw.mark_age_seconds)
        : null,
  };
  return isSignCompletePosition(row) ? row : null;
};

const samePositionScope = (row: AnyRecord, incoming: AnyRecord): boolean => {
  for (const key of ["environment", "broker_name", "broker_user_id"]) {
    if (row[key] != null && incoming[key] != null && String(row[key]) !== String(incoming[key])) return false;
  }
  // REST broker_account_id is the local PK. Delta account_id is the local PK.
  const local = row.account_id ?? row.broker_account_id;
  if (local != null && incoming.account_id != null && String(local) !== String(incoming.account_id)) return false;
  const provider = row.broker_metadata?.accountId;
  const incomingProvider = incoming.provider_account_id ?? incoming.broker_metadata?.accountId ??
    (incoming.id == null ? incoming.broker_account_id : null);
  if (provider != null && incomingProvider != null && String(provider) !== String(incomingProvider)) return false;
  return true;
};

const snapshotRowMatch = (row: AnyRecord, incoming: AnyRecord): boolean => {
  if (!samePositionScope(row, incoming)) return false;
  if (incoming.id != null && String(row.id ?? row.position_id) === String(incoming.id)) return true;
  return incoming.broker_position_id != null && row.broker_position_id != null &&
    String(row.broker_position_id) === String(incoming.broker_position_id);
};

/** Merge a completed REST request with events accepted while it was in flight.
 * Missing rows still close the book, except rows opened after this request began.
 */
export const reconcilePositionSnapshot = <T,>(current: unknown, incoming: T, atStart?: unknown): T => {
  const next = getPositionListContainer(incoming as AnyRecord);
  const previous = getPositionListContainer(current as AnyRecord);
  if (!next || !previous) return incoming;
  const started = getPositionListContainer(atStart as AnyRecord)?.positions ?? [];
  const rows = next.positions.flatMap((row) => {
    const old = previous.positions.find((candidate) => snapshotRowMatch(candidate, row));
    const original = started.find((candidate) => snapshotRowMatch(candidate, row));
    // A close accepted during the request must not be resurrected by its response.
    if (!old && original) return [];
    if (!old) return [row];
    // Legacy profile snapshots lack mark versions. They cannot erase a
    // canonical observation; absence from the authoritative book still closes it.
    if (asObservedSeq(old.observed_seq) && !asObservedSeq(row.observed_seq) &&
        parseMeasuredAt(row.mark_measured_at) === null) return [old];
    if (old.lifecycle_key && row.lifecycle_key && old.lifecycle_key !== row.lifecycle_key) {
      return [isNewerOpenedLifecycle(old, row) ? row : old];
    }
    return [rejectsLifecycleDelta(old, row) ? old : row];
  });
  for (const old of previous.positions) {
    if (!rows.some((row) => snapshotRowMatch(old, row)) &&
        !started.some((row) => snapshotRowMatch(old, row))) rows.push(old);
  }
  const rebuilt = next.rebuild(rows);
  const countDelta = rows.length - next.positions.length;
  if (!Array.isArray(rebuilt) && countDelta !== 0) {
    for (const key of ["total", "count"]) {
      if (typeof rebuilt[key] === "number") rebuilt[key] = Math.max(rows.length, rebuilt[key] + countDelta);
    }
  }
  return rebuilt as T;
};

/**
 * Hydrate-from-snapshot-first: upsert sign-complete snapshot rows into the
 * open book. Empty payloads are absorbed (never wipe). Rows already in cache
 * keep live quote marks via mergeDelta. Rows not in the snapshot stay
 * (a live open can race the snapshot).
 */
const applyPositionsSnapshot = (
  message: AnyRecord,
  queryClient: QueryClient,
): boolean => {
  const rawRows = extractSnapshotRows(message);
  if (rawRows === null) return false;
  const incoming = rawRows
    .map(normalizeSnapshotRow)
    .filter((row): row is AnyRecord => row != null && !wasRecentlyClosed(queryClient, row.id, row.lifecycle_key));
  // Error/empty snapshot must not blank a painted book.
  if (incoming.length === 0) return true;

  queryClient.setQueriesData(
    { predicate: (query) => (query.queryKey[0] === "positions" || (query.queryKey[0] === "profile-positions" && query.state.data !== undefined)) },
    (oldData: AnyRecord | undefined) => {
      const container = getPositionListContainer(oldData);
      const seedShape = (positions: AnyRecord[]) => ({
        ...(oldData && typeof oldData === "object" ? oldData : {}),
        items: positions,
        results: {
          ...((oldData as AnyRecord | undefined)?.results ?? {}),
          positions,
        },
        total: positions.length,
        count: positions.length,
      });
      if (!container) {
        if (oldData !== undefined && oldData !== null) return oldData;
        return seedShape(incoming);
      }
      const rows = [...container.positions];
      let added = 0;
      for (const next of incoming) {
        const idx = rows.findIndex((row) => snapshotRowMatch(row, next));
        if (idx >= 0) {
          const old = rows[idx];
          const changedLife = old.lifecycle_key && next.lifecycle_key && old.lifecycle_key !== next.lifecycle_key;
          rows[idx] = changedLife
            ? (isNewerOpenedLifecycle(old, next) ? next : old)
            : mergeDelta(old, { ...next, side: next.direction });
        } else {
          rows.push(next);
          added += 1;
        }
      }
      const rebuilt = container.rebuild(rows);
      if (Array.isArray(rebuilt)) return rebuilt;
      return {
        ...rebuilt,
        ...(typeof oldData?.total === "number"
          ? { total: oldData.total + added }
          : {}),
        ...(typeof oldData?.count === "number"
          ? { count: oldData.count + added }
          : {}),
      };
    },
  );
  return true;
};

/**
 * Apply a positions-channel delta into every cached positions query.
 * Returns true when the event was recognized and applied (or safely absorbed);
 * false when the caller should fall back to invalidation-only.
 */
export const applyPositionsChannelMessage = (
  message: AnyRecord,
  queryClient: QueryClient,
  opts?: {
    /** Flatten-hardening contract (b): position deltas that ARE the
     * liquidation's own progress (emergency.position_liquidated) must not
     * retire the live flatten attempt — only ADVERSE position truth does.
     * Defaults true (any ordinary position activity revokes/retires). */
    retireFlattenAttempt?: boolean;
  },
): boolean => {
  if (opts?.retireFlattenAttempt !== false) {
    markGlobalFlattenUnknown(queryClient);
  }
  // `positions.initial`.startsWith("position.") is true — check snapshot
  // before treating the frame as a single-row delta.
  if (applyPositionsSnapshot(message, queryClient)) {
    return true;
  }
  const event = extractPositionEvent(message);
  if (!event) {
    // F1 (ledger item 12): SIG-688 broker_entity mirrors carry no
    // position_id but ARE matchable by broker_position_id — apply them to
    // known rows instead of forcing a REST round-trip on every mirror.
    const brokerEntity = extractBrokerEntityPosition(message);
    if (brokerEntity) {
      return applyBrokerEntityPosition(
        brokerEntity,
        queryClient,
        message?.payload ?? message?.data ?? {},
      );
    }
    return false; // unknown shapes → let refetch own it
  }

  const { action, payload } = event;
  const id = payload.position_id;
  if (!payload.__broker_quote_flush && isBrokerQuoteOnly(action, payload)) {
    scheduleBrokerQuote(queryClient, payload);
    return true;
  }
  const wasClosedBeforeEvent =
    action === "closed" && wasRecentlyClosed(queryClient, id, payload.lifecycle_key);
  let touched = false;
  let rejectedClose = false;

  if (action === "opened") {
    const openedPosition = buildOpenedPosition(payload);
    if (!openedPosition) return false;
    // SIG-1749: never insert a row whose account is absent from this
    // member's already-loaded accounts cache. Shared-identity sockets can
    // emit a foreign position.opened a beat before REST overwrites the
    // book — that flash is the tenant leak. Unknown/unloaded accounts
    // cache → leave the insert (server scope is the authority).
    if (!openedAccountIsKnownToMember(queryClient, payload)) {
      return false;
    }
    let refusedUnorderedLifecycle = false;
    queryClient.setQueriesData(
      {
        queryKey: ["positions"],
        predicate: (query) => openedEventMatchesQuery(query, payload),
      },
      (oldData: AnyRecord | undefined) => {
        const container = getPositionListContainer(oldData);
        if (!container) return oldData;
        const optimisticIndex = container.positions.findIndex((position) =>
          optimisticMatchesOpened(position, payload),
        );
        if (optimisticIndex >= 0) {
          touched = true;
          return container.rebuild(
            container.positions.map((position, index) =>
              index === optimisticIndex ? openedPosition : position,
            ),
          );
        }
        const alreadyPresent = container.positions.some(
          (position) =>
            position?.id === id ||
            position?.position_id === id ||
            String(position?.id) === String(id),
        );
        if (alreadyPresent) {
          return container.rebuild(
            container.positions.map((position) => {
              const matchesPosition =
                position?.id === id ||
                position?.position_id === id ||
                String(position?.id) === String(id);
              if (!matchesPosition) return position;
              const lifecycleChanged =
                asObservedSeq(position.lifecycle_key) !== null &&
                asObservedSeq(payload.lifecycle_key) !== null &&
                asObservedSeq(position.lifecycle_key) !==
                  asObservedSeq(payload.lifecycle_key);
              if (lifecycleChanged && !isNewerOpenedLifecycle(position, payload)) {
                refusedUnorderedLifecycle = true;
                return position;
              }
              touched = true;
              return lifecycleChanged
                ? openedPosition
                : mergeDelta(position, payload);
            }),
          );
        }

        touched = true;
        const rebuilt = container.rebuild([
          openedPosition,
          ...container.positions,
        ]);
        return {
          ...rebuilt,
          ...(typeof oldData?.total === "number"
            ? { total: oldData.total + 1 }
            : {}),
          ...(typeof oldData?.count === "number"
            ? { count: oldData.count + 1 }
            : {}),
        };
      },
    );
    return touched && !refusedUnorderedLifecycle;
  }

  queryClient.setQueriesData(
    { predicate: (query) => (query.queryKey[0] === "positions" || (query.queryKey[0] === "profile-positions" && query.state.data !== undefined)) },
    (oldData: AnyRecord | undefined) => {
      const container = getPositionListContainer(oldData);
      if (!container) {
        if (oldData && process.env.NODE_ENV !== "production") {
          // SIG-345 probe: an unrecognized cache SHAPE bails before the row
          // lookup, so the delta silently never applies to that query.
          console.warn(
            `[SIG-345] positions delta '${action}': unrecognized cache shape, keys=` +
              JSON.stringify(Object.keys(oldData as AnyRecord).slice(0, 8)),
          );
        }
        return oldData;
      }
      const rows = container.positions;
      const idx = rows.findIndex(
        (p) =>
          p?.id === id || p?.position_id === id || String(p?.id) === String(id),
      );
      if (idx === -1 && process.env.NODE_ENV !== "production") {
        // A delta for a non-selected follower legitimately misses this account-
        // filtered cache. Preserve the immediate truth refetch below, but keep
        // the diagnostic at debug severity so fleet fan-out does not look like
        // an operator-facing cache fault.
        console.debug(
          `[SIG-345] positions delta '${action}' found no row for id=${JSON.stringify(id)} ` +
            `(${typeof id}); cache had ${rows.length} row(s) with ids=` +
            JSON.stringify(
              rows.slice(0, 5).map((p) => [p?.id, p?.position_id, p?.status]),
            ),
        );
      }

      if (action === "closed") {
        if (idx === -1) return oldData;
        if (!samePositionScope(rows[idx], payload) || rejectsLifecycleDelta(rows[idx], payload)) {
          rejectedClose = true;
          return oldData;
        }
        touched = true;
        // Open-positions lists drop the row (auto-animate slides it out);
        // mixed lists keep it with closed status.
        return container.rebuild(closeRowInList(rows, idx, payload));
      }

      if (idx >= 0) {
        touched = true;
        return container.rebuild(
          rows.map((p, i) => (i === idx ? mergeDelta(p, payload) : p)),
        );
      }
      // Update for a row we do not have: let the caller invalidate immediately.
      return oldData;
    },
  );

  // Record even when no cache held the row (already dropped, filtered cache):
  // the F3 guard below must still refuse to resurrect this id.
  if (action === "closed") {
    const hasStablePositionId =
      (typeof id === "number" && Number.isFinite(id) && id > 0) ||
      (typeof id === "string" && id.trim() !== "" && id.trim() !== "0");
    if (touched && hasStablePositionId && !wasClosedBeforeEvent) {
      transferClosedPositionRealizedPnl(queryClient, {
        accountId: payload.account_id,
        realizedPnl: payload.realized_pnl,
      });
    }
    if (!rejectedClose) noteRecentlyClosed(queryClient, id, payload.lifecycle_key);
  }

  // F3 (ledger item 12): the backend can emit `updated` BEFORE `opened`
  // (updated-first ordering out of the diff publisher), so an unknown-id
  // update whose payload already carries a constructible open row used to
  // wait out a REST round-trip. Re-dispatch it as opened: buildOpenedPosition
  // fail-closes (qty>0, entry>0, side, account) and the opened path inserts
  // only into caches whose filters match — anything short of that still
  // reports unhandled for the immediate refetch.
  // Guards against resurrection: an explicit non-open status never takes the
  // path, and neither does an id we closed within the last 10s — production
  // updated frames carry NO status field, so the recently-closed guard is
  // what actually catches a stale updated delivered after closed.
  const updatedStatus = String(payload.status ?? "")
    .trim()
    .toLowerCase();
  if (
    !touched &&
    action === "updated" &&
    (updatedStatus === "" || ["open", "active"].includes(updatedStatus)) &&
    !wasRecentlyClosed(queryClient, id, payload.lifecycle_key)
  ) {
    return applyPositionsChannelMessage(
      { type: "position.opened", payload },
      queryClient,
    );
  }

  // SIG-345: "handled" must mean we ACTUALLY patched a row. Claiming handled
  // for an unmatched close/update (id-shape mismatch, row not in this cache)
  // downgraded the refresh from the immediate path to the 1.5s trailing
  // debounce — measured 2-4s for the close leg vs <1s for the open leg
  // (contract A1 requires both <1s). Report the truth and let the caller
  // refetch now.
  return touched;
};

/** Apply two-phase emergency progress without representing intent as a fill. */
export const applyEmergencyChannelMessage = (
  message: AnyRecord,
  queryClient: QueryClient,
  currentSubject?: string | null,
): boolean => {
  const eventType = String(message?.type ?? message?.event_type ?? "");
  const payload: AnyRecord = message?.payload ?? message?.data ?? {};

  if (eventType === "risk.alert") {
    const severity = String(payload.severity ?? "warning").toLowerCase();
    const message = String(
      payload.message ?? payload.alert_type ?? "A risk protection fired.",
    );
    const isHalt = severity === "critical";
    // Emergency frames carry no stable id — identical content toasts once
    // per window (the recurring cash-exit-warning storm class).
    // Key includes severity: a critical MUST break through even when a
    // warning with identical copy toasted moments earlier.
    if (shouldToastOnce(`risk.alert:${severity}:${message}`)) {
      if (isHalt || severity === "error") {
        captureAlertToast(`risk.alert: ${message}`, payload);
      }
      toast({
        variant: isHalt ? "destructive" : "default",
        title: isHalt ? "Trading paused" : "Risk alert",
        description: isHalt
          ? `${message} New trades are paused to protect your accounts. Open positions stay managed. You do not need to enable a trading group.`
          : message,
      });
    }
    void queryClient.invalidateQueries({ queryKey: ["risk"] });
    return true;
  }

  if (eventType === "cash_exit.completed") {
    const positionsClosed = Number(payload.positions_closed ?? 0);
    // GA-8: honor the backend's success flag. success === false means the
    // liquidation did NOT fully land — saying "completed / flat" there is a
    // money-path lie. An event WITHOUT the flag (older backend) proves flat
    // only when the count is positive; zero closes gets neutral copy, never
    // a flat claim the payload cannot back.
    const succeeded = payload.success;
    if (shouldToastOnce(`cash_exit.completed:${positionsClosed}:${String(succeeded)}`, 60_000)) {
      if (succeeded === false) {
        toast({
          variant: "warning",
          title: "Cash exit did not fully complete",
          description: `${positionsClosed} position${positionsClosed === 1 ? "" : "s"} closed — some may remain open. Check your positions.`,
        });
      } else {
        toast({
          title: "Cash exit completed",
          description:
            positionsClosed > 0
              ? `${positionsClosed} position${positionsClosed === 1 ? "" : "s"} closed.`
              : "No open positions needed closing.",
        });
      }
    }
    void queryClient.invalidateQueries({ queryKey: ["positions"] });
    return true;
  }

  if (eventType === "emergency.started" || eventType === "emergency.progress") {
    const actionType = String(payload.action_type ?? "").toUpperCase();
    const closePositions = payload.parameters?.close_positions === true;
    if (
      actionType === "POSITION_LIQUIDATION" ||
      (actionType === "EMERGENCY_STOP" && closePositions)
    ) {
      // Flatten-hardening contract (c): FRAMES NEVER MINT AUTHORITY. The
      // only begin is the tab's own accepted HTTP request (positions.service
      // flatten lane). A replayed/relayed emergency.started used to re-begin
      // a FRESH attempt after activity had retired the HTTP one — the
      // replayed completion then matched it and a false proven_flat could
      // disable the global-flatten control on a non-flat book (Tier A
      // violation). A started frame now only revokes any stale proof when no
      // live local attempt matches it.
      if (
        eventType === "emergency.started" &&
        currentSubject &&
        payload.execution_id !== undefined &&
        payload.execution_id !== null &&
        hasLiveGlobalFlattenAttempt(queryClient, {
          executionId: payload.execution_id,
          subject: currentSubject,
          mode: "live",
        })
      ) {
        // Our own in-flight attempt's progress — keep it; the closing paint
        // below is the visible half.
      } else if (currentSubject) {
        markGlobalFlattenUnknown(queryClient, currentSubject);
      } else {
        markGlobalFlattenUnknown(queryClient);
      }
      markOpenPositionsClosing(queryClient);
    }
    queryClient.setQueryData(
      ["admin-circuit-breaker"],
      (oldData: AnyRecord | undefined) =>
        oldData
          ? {
              ...oldData,
              state: "OPEN",
              reason: `${actionType || "Emergency action"} armed`,
            }
          : oldData,
    );
    return true;
  }

  if (eventType === "emergency.position_liquidated") {
    // Broker-only rows (no SQL id) used to match nothing and the frame was
    // silently dropped — fall back to clearing the account's liquidating
    // rows so the table drains instead of freezing on LIQUIDATING.
    if (payload.position_id == null) {
      return closeLiquidatingRowsForAccount(queryClient, payload.broker_account_id);
    }
    // The liquidation's own per-leg progress — it must not retire the live
    // attempt it belongs to (contract b: the attempt survives to be
    // completed by the broker-safe proof).
    return applyPositionsChannelMessage(
      { type: "position.closed", payload: { ...payload, quantity: 0 } },
      queryClient,
      { retireFlattenAttempt: false },
    );
  }

  if (eventType === "emergency.account_flattened") {
    // Per-account progress inside the flatten gather (2026-08-18): each
    // account's rows drain the moment ITS legs are broker-settled, instead
    // of every row sitting LIQUIDATING until the whole wave finishes.
    if (payload.broker_flat === true) {
      closeLiquidatingRowsForAccount(queryClient, payload.broker_account_id);
    }
    return true;
  }

  if (eventType === "emergency.liquidation_recovered") {
    // TWO publishers, different guarantees — never conflate them:
    //  • broker_flat === true (FAILED-but-flat arm release): a GLOBAL
    //    broker-flat proof ran — safe to drain every liquidating row.
    //  • otherwise (stale-liquidation unstick): positions were only moved
    //    liquidating→closing to clear a dead worker's paint; they are NOT
    //    proven flat — just clear the stuck optimistic marker and let REST
    //    reconcile; NEVER mark them closed.
    if (payload.broker_flat === true) {
      closeLiquidatingRowsForAccount(queryClient, null);
      if (shouldToastOnce("emergency.liquidation_recovered", 60_000)) {
        toast({
          title: "Emergency close verified complete",
          description:
            "Your accounts are confirmed flat at the broker. Trading controls released.",
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["admin-circuit-breaker"] });
    }
    void queryClient.invalidateQueries({ queryKey: ["positions"] });
    return true;
  }

  if (eventType === "emergency_flatten_unarmed") {
    // No broker liquidation was scheduled — the optimistic LIQUIDATING paint
    // must unwind, not persist as a phantom in-flight close.
    let touched = false;
    rebuildPositionCaches(queryClient, (position) => {
      if (position?.optimistic_emergency !== true) return position;
      touched = true;
      const {
        execution_state: _executionState,
        optimistic_emergency: _optimisticEmergency,
        ...rest
      } = position;
      return { ...rest, status: "active" };
    });
    void queryClient.invalidateQueries({ queryKey: ["positions"] });
    return touched;
  }

  if (eventType === "emergency.position_liquidation_failed") {
    const targetId = payload.position_id;
    const failureReason = String(payload.reason ?? "").trim();
    if (
      failureReason &&
      shouldToastOnce(`emergency.liq_failed:${String(targetId)}:${failureReason}`, 60_000)
    ) {
      toast({
        variant: "destructive",
        title: "Emergency close failed",
        description: `${String(payload.symbol ?? "Position")}: ${failureReason}`,
      });
    }
    let touched = false;
    rebuildPositionCaches(queryClient, (position) => {
      if (String(position?.id ?? position?.position_id) !== String(targetId)) {
        return position;
      }
      touched = true;
      // TS6 drops the Record index signature when spreading into a literal,
      // so `delete restored.execution_state` no longer type-checks — strip the
      // optimistic markers by destructuring instead (same runtime result).
      const {
        execution_state: _executionState,
        optimistic_emergency: _optimisticEmergency,
        ...rest
      } = position;
      return { ...rest, status: "active" };
    });
    return touched;
  }

  if (eventType === "emergency.completed" || eventType === "emergency.error") {
    if (
      eventType === "emergency.completed" &&
      String(payload.action_type ?? "").toUpperCase() ===
        "POSITION_LIQUIDATION" &&
      String(payload.status ?? "").toUpperCase() === "COMPLETED" &&
      payload.broker_safe_state === "VERIFIED_FLAT_NO_LIVE_ORDERS" &&
      payload.proof_scope === "global" &&
      typeof payload.proof_subject === "string" &&
      payload.proof_subject === currentSubject &&
      typeof payload.proof_epoch === "string" &&
      payload.proof_epoch.length > 0
    ) {
      completeGlobalFlattenAttempt(queryClient, {
        executionId: payload.execution_id,
        source: "broker",
        mode: "live",
        proofScope: "global",
        subject: payload.proof_subject,
        proofEpoch: payload.proof_epoch,
      });
    } else {
      markGlobalFlattenUnknown(queryClient);
    }
    void queryClient.invalidateQueries({ queryKey: ["positions"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-circuit-breaker"] });
    return true;
  }

  return false;
};

/**
 * Trailing-debounce factory for the broad trading invalidation: a burst of N
 * events inside `waitMs` causes exactly ONE reconciliation refetch at the end.
 */
export const makeTrailingInvalidator = (
  fn: () => void,
  waitMs = 1500,
): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn();
    }, waitMs);
  };
};
